import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import { buildWhatsAppUrl } from '../utils/whatsapp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Elegant placeholder URLs — Unsplash editorial library, neutral landscape compositions.
// Each will be replaced by the client's own photography in a second phase.
const PLACEHOLDER = {
  hero: '/team/hero-cascais.jpg',
  portugal: 'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?auto=format&fit=crop&w=1800&q=85',
  founders: '/team/founders-diptych.jpg',
  opp1: '/mandates/aquisicao.jpg',
  opp2: '/mandates/investimento.jpg',
  opp3: '/mandates/branded.jpg',
};

const REGION_PLACEHOLDERS = {
  'Lisboa Prime': 'https://images.unsplash.com/photo-1588001400947-6385aef4ab0e?auto=format&fit=crop&w=900&q=85',
  'Cascais & Oeiras': 'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?auto=format&fit=crop&w=900&q=85',
  'Comporta & Alentejo': 'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=900&q=85',
  'Algarve': 'https://images.unsplash.com/photo-1537254489012-e2c5ee6b8df6?auto=format&fit=crop&w=900&q=85',
  'Porto & Norte': 'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?auto=format&fit=crop&w=900&q=85',
  'Costa Atlântica & Surf': 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=900&q=85',
};

const COPY = {
  pt: {
    heroEyebrow: 'Private Real Estate Advisory · Portugal',
    heroTitle: 'Onde a discrição encontra o',
    heroTitleEm: 'imobiliário excepcional',
    heroSub: 'Representamos exclusivamente compradores internacionais. Sem listagens. Sem transações. Uma relação consultiva privada construída sobre confiança, inteligência e acesso curado.',
    heroCta: 'Iniciar conversa',
    heroCtaSec: 'A nossa abordagem',
    heroCtaWa: 'Falar agora · WhatsApp',
    manifestoEyebrow: 'Manifesto',
    manifestoTitle: 'Não somos uma imobiliária.',
    manifestoTitleEm: 'Nem queremos ser.',
    manifestoBody1: 'Existimos para quem compra. Para quem procura clareza onde há ruído, critério onde há catálogo, acesso onde há barreira.',
    manifestoBody2: 'Não temos imóveis. Temos rede. Não temos preços fixos. Temos negociação. Não trabalhamos para vendedores. Trabalhamos para si.',
    manifestoQuote: 'O nosso compromisso é estar do seu lado da mesa — sempre, sem exceções, sem comissões de vendedor.',
    approachEyebrow: 'A nossa abordagem',
    approachTitle: 'Cinco passos, uma relação.',
    approachSteps: [
      { num: '01', label: 'Briefing privado', body: 'Conversa inicial para compreender perfil, objetivos, prazo e o que verdadeiramente importa.' },
      { num: '02', label: 'Curadoria silenciosa', body: 'Acesso à nossa rede de promotores, mediadoras e vendedores diretos. Filtragem rigorosa para apresentar três a quatro oportunidades.' },
      { num: '03', label: 'Acesso off-market', body: 'Imóveis nunca publicados. Acordos discretos com vendedores qualificados. Condições reservadas a quem é apresentado por nós.' },
      { num: '04', label: 'Negociação independente', body: 'Representamos o comprador. Sempre. Sem comissões de vendedor. Clareza, transparência, alinhamento.' },
      { num: '05', label: 'Due diligence & fecho', body: 'Coordenamos advogados, técnicos e instituições financeiras. Acompanhamos cada passo até à entrega de chaves.' },
    ],
    regionsEyebrow: 'Geografias',
    regionsTitle: 'Onde representamos os nossos clientes.',
    regionsCta: 'Ver todas as regiões',
    opportunitiesEyebrow: 'Tipos de mandato',
    opportunitiesTitle: 'Três naturezas. Um mesmo critério.',
    opportunitiesSub: 'Cada mandato que aceitamos enquadra-se numa destas três categorias. A escolha define o método, o calendário e os profissionais envolvidos.',
    opportunitiesEnquiry: 'Saber mais',
    opportunitiesTypes: {
      large: {
        eyebrow: 'Residência principal · Segunda habitação',
        title: 'Aquisição residencial.',
        body: 'Para clientes internacionais que escolhem Portugal como lar — primário ou alternativo. A análise vai além do imóvel: bairro, comunidade, escolas, serviços, fiscalidade e o ajuste à vida que querem viver.',
      },
      small1: {
        eyebrow: 'Yield · Valorização',
        title: 'Investimento patrimonial.',
        body: 'Mandatos com lente financeira: yield, exit e diversificação de portfolio internacional.',
      },
      small2: {
        eyebrow: 'Coleccionismo · Brand',
        title: 'Branded residence.',
        body: 'Residências em desenvolvimentos com gestão hoteleira de marcas internacionais reconhecidas.',
      },
    },
    foundersEyebrow: 'Founders',
    foundersTitle: 'Fundada por quem viveu o mercado dos dois lados.',
    foundersBody: 'Alex Kühne e Fernanda Stelzer fundaram a Your Broker Portugal com uma convicção: faltava em Portugal uma estrutura que representasse exclusivamente o comprador internacional, com a profundidade de mercado e sofisticação editorial que estes clientes encontram nas grandes praças europeias.',
    foundersAlex: { name: 'Alex Kühne', role: 'Co-fundador. Mercados imobiliários internacionais.' },
    foundersFer: { name: 'Fernanda Stelzer', role: 'Co-fundadora. Clientes brasileiros e europeus.' },
    journalEyebrow: 'Journal',
    journalTitle: 'Notas, leituras e perspectivas.',
    journalCta: 'Ler todos os ensaios',
    closingTitle: 'Toda relação começa por uma conversa.',
    closingBody: 'Se considera comprar em Portugal — para viver, investir, ou diversificar — começamos por uma conversa privada, sem agenda comercial.',
    closingCta: 'Iniciar conversa',
    closingEmail: 'portugal@yourbrokerportugal.com',
    closingPhone: '+351 931 997 415',
    closingAmi: 'AMI 19715 · NIPC 516 549 642',
  },
  en: {
    heroEyebrow: 'Private Real Estate Advisory · Portugal',
    heroTitle: 'Where discretion meets',
    heroTitleEm: 'exceptional real estate',
    heroSub: 'We represent international buyers exclusively. Not listings. Not transactions. A private advisory relationship built on trust, intelligence and curated access.',
    heroCta: 'Begin a conversation',
    heroCtaSec: 'Our approach',
    heroCtaWa: 'Chat now · WhatsApp',
    manifestoEyebrow: 'Manifesto',
    manifestoTitle: 'We are not a real estate agency.',
    manifestoTitleEm: 'Nor do we wish to be.',
    manifestoBody1: 'We exist for those who buy. For those seeking clarity where there is noise, criteria where there is catalogue, access where there is barrier.',
    manifestoBody2: 'We hold no properties. We hold a network. We hold no fixed prices. We negotiate. We do not work for sellers. We work for you.',
    manifestoQuote: 'Our commitment is to remain on your side of the table — always, without exception, without seller commission.',
    approachEyebrow: 'Our approach',
    approachTitle: 'Five steps, one relationship.',
    approachSteps: [
      { num: '01', label: 'Private briefing', body: 'Initial conversation to understand profile, objectives, timeframe, and what truly matters.' },
      { num: '02', label: 'Quiet curation', body: 'Access to our network of developers, agencies and direct sellers. Rigorous filtering to present three to four opportunities.' },
      { num: '03', label: 'Off-market access', body: 'Properties never published. Discreet arrangements with qualified sellers. Terms reserved for those introduced by us.' },
      { num: '04', label: 'Independent negotiation', body: 'We represent the buyer. Always. No seller commissions. Clarity, transparency, alignment.' },
      { num: '05', label: 'Due diligence & closing', body: 'We coordinate lawyers, technical experts and financial institutions. We follow every step through to handover.' },
    ],
    regionsEyebrow: 'Geographies',
    regionsTitle: 'Where we represent our clients.',
    regionsCta: 'View all regions',
    opportunitiesEyebrow: 'Mandate types',
    opportunitiesTitle: 'Three natures. One criterion.',
    opportunitiesSub: 'Every mandate we accept falls into one of these three categories. The choice defines the method, the timeline and the professionals involved.',
    opportunitiesEnquiry: 'Learn more',
    opportunitiesTypes: {
      large: {
        eyebrow: 'Primary residence · Second home',
        title: 'Residential acquisition.',
        body: 'For international clients who choose Portugal as home — primary or alternative. The analysis extends beyond the property: neighbourhood, community, schools, services, taxation and the fit to the life they wish to live.',
      },
      small1: {
        eyebrow: 'Yield · Capital appreciation',
        title: 'Patrimonial investment.',
        body: 'Mandates with a financial lens: yield, exit and international portfolio diversification.',
      },
      small2: {
        eyebrow: 'Collecting · Brand',
        title: 'Branded residence.',
        body: 'Residences in developments with hotel management by recognised international brands.',
      },
    },
    foundersEyebrow: 'Founders',
    foundersTitle: 'Founded by those who lived the market from both sides.',
    foundersBody: 'Alex Kühne and Fernanda Stelzer founded Your Broker Portugal from a single conviction: Portugal lacked a firm exclusively representing the international buyer, with the market depth and editorial sophistication these clients find in major European capitals.',
    foundersAlex: { name: 'Alex Kühne', role: 'Co-founder. International real estate markets.' },
    foundersFer: { name: 'Fernanda Stelzer', role: 'Co-founder. Brazilian and European clients.' },
    journalEyebrow: 'Journal',
    journalTitle: 'Notes, readings, perspectives.',
    journalCta: 'Read all essays',
    closingTitle: 'Every relationship begins with a conversation.',
    closingBody: 'If you are considering buying in Portugal — to live, to invest, or to diversify — we begin with a private conversation, without a commercial agenda.',
    closingCta: 'Begin a conversation',
    closingEmail: 'portugal@yourbrokerportugal.com',
    closingPhone: '+351 931 997 415',
    closingAmi: 'AMI 19715 · NIPC 516 549 642',
  },
};

// Reveal-on-scroll using IntersectionObserver.
// `deps` re-runs the observer when async content (posts/regions) arrives after mount.
const useRevealOnScroll = (deps = []) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -10% 0px' }
    );
    el.querySelectorAll('.rv:not(.is-visible)').forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, deps);
  return ref;
};

const Eyebrow = ({ children, dark = false }) => (
  <p
    className={`inline-flex items-center gap-3 text-[11px] font-medium uppercase font-sans ${
      dark ? 'text-editorial-bronze-lt' : 'text-editorial-bronze'
    }`}
    style={{ letterSpacing: '0.24em', fontFamily: 'DM Sans, sans-serif' }}
  >
    <span className={`block w-7 h-px ${dark ? 'bg-editorial-bronze-lt' : 'bg-editorial-bronze'}`} />
    {children}
  </p>
);

const SectionPad = ({ children, dark = false, className = '' }) => (
  <section
    className={`px-6 md:px-10 lg:px-16 xl:px-24 py-20 md:py-28 lg:py-36 ${
      dark ? 'bg-editorial-charcoal text-editorial-bone' : 'bg-editorial-mineral text-editorial-charcoal'
    } ${className}`}
  >
    {children}
  </section>
);

// ─────────────────────────────────────────
// SECTIONS
// ─────────────────────────────────────────

const Hero = ({ image, copy, localizedPath, language }) => (
  <section className="relative h-screen min-h-[640px] flex items-end overflow-hidden" data-testid="hero">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{
        backgroundImage: `url(${image})`,
        backgroundPosition: 'center 30%',
        animation: 'heroZoom 14s ease-out forwards',
      }}
    />
    <div
      className="absolute inset-0"
      style={{
        background: 'linear-gradient(to top, rgba(18,16,12,0.92) 0%, rgba(18,16,12,0.62) 45%, rgba(18,16,12,0.32) 78%, rgba(18,16,12,0.18) 100%)',
      }}
    />
    <div className="relative z-10 px-6 md:px-10 lg:px-16 xl:px-24 pb-20 md:pb-28 max-w-[820px]">
      <div className="hero-eyebrow opacity-0" style={{ animation: 'fadeUp 1s 0.3s ease forwards' }}>
        <Eyebrow dark>{copy.heroEyebrow}</Eyebrow>
      </div>
      <h1
        className="hero-h1 opacity-0 text-editorial-bone font-light mt-7"
        style={{
          fontFamily: 'Cormorant Garamond, serif',
          fontSize: 'clamp(2.8rem, 5.5vw, 4.8rem)',
          lineHeight: '1.04',
          animation: 'fadeUp 1s 0.55s ease forwards',
        }}
      >
        {copy.heroTitle}{' '}
        <em className="italic text-editorial-sand-deep">{copy.heroTitleEm}</em>.
      </h1>
      <p
        className="hero-sub opacity-0 text-editorial-bone/85 mt-7 max-w-[500px] font-light leading-[1.92]"
        style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', animation: 'fadeUp 1s 0.8s ease forwards', textShadow: '0 1px 16px rgba(0,0,0,0.4)' }}
      >
        {copy.heroSub}
      </p>
      <div
        className="hero-actions opacity-0 mt-10 flex flex-col sm:flex-row gap-6 sm:items-center"
        style={{ animation: 'fadeUp 1s 1.0s ease forwards' }}
      >
        <Link
          to={localizedPath('/contact')}
          className="inline-flex items-center text-editorial-charcoal bg-editorial-sand hover:bg-editorial-bone transition-all duration-300 hover:-translate-y-0.5 py-4 px-10 font-medium"
          style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '0.65rem',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            borderRadius: 0,
          }}
          data-testid="hero-cta-primary"
        >
          {copy.heroCta}
        </Link>
        <a
          href={buildWhatsAppUrl('home', language)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-editorial-bone/58 hover:text-editorial-bone border-b border-editorial-bone/22 hover:border-editorial-bone pb-0.5 transition-colors"
          style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '0.65rem',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
          }}
          data-testid="hero-cta-whatsapp"
        >
          {copy.heroCtaWa}
        </a>
      </div>
    </div>
  </section>
);

const Manifesto = ({ copy }) => (
  <SectionPad>
    <div className="max-w-5xl mx-auto">
      <div className="rv">
        <Eyebrow>{copy.manifestoEyebrow}</Eyebrow>
      </div>
      <h2
        className="rv text-editorial-charcoal font-light mt-7 max-w-3xl"
        style={{
          fontFamily: 'Cormorant Garamond, serif',
          fontSize: 'clamp(2rem, 3.2vw, 3rem)',
          lineHeight: '1.18',
        }}
      >
        {copy.manifestoTitle}{' '}
        <em className="italic text-editorial-bronze">{copy.manifestoTitleEm}</em>
      </h2>

      <div
        className="rv max-w-2xl mt-12 space-y-7 text-editorial-muted font-light"
        style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
      >
        <p>{copy.manifestoBody1}</p>
        <p>{copy.manifestoBody2}</p>
      </div>

      <blockquote
        className="rv mt-14 max-w-2xl pl-7 italic text-editorial-charcoal-soft"
        style={{
          fontFamily: 'Cormorant Garamond, serif',
          fontSize: '1.35rem',
          fontWeight: 300,
          lineHeight: '1.6',
          borderLeft: '2px solid #b8955a',
        }}
      >
        {copy.manifestoQuote}
      </blockquote>
    </div>
  </SectionPad>
);

const Approach = ({ copy }) => (
  <SectionPad className="bg-editorial-sand/50" >
    <div id="approach" className="max-w-5xl mx-auto">
      <div className="rv">
        <Eyebrow>{copy.approachEyebrow}</Eyebrow>
      </div>
      <h2
        className="rv text-editorial-charcoal font-light mt-7 mb-14 max-w-3xl"
        style={{
          fontFamily: 'Cormorant Garamond, serif',
          fontSize: 'clamp(2rem, 3.2vw, 3rem)',
          lineHeight: '1.18',
        }}
      >
        {copy.approachTitle}
      </h2>

      <div>
        {copy.approachSteps.map((s) => (
          <article
            key={s.num}
            className="rv grid grid-cols-12 gap-6 md:gap-10 py-10 md:py-12 border-b"
            style={{ borderColor: 'rgba(28,28,26,0.08)' }}
            data-testid={`approach-${s.num}`}
          >
            <div className="col-span-12 md:col-span-2">
              <span
                className="text-editorial-sand-deep font-light"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '3.4rem', lineHeight: 1 }}
              >
                {s.num}
              </span>
            </div>
            <div className="col-span-12 md:col-span-3">
              <h3
                className="text-editorial-charcoal font-normal"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.45rem', lineHeight: '1.18' }}
              >
                {s.label}
              </h3>
            </div>
            <div
              className="col-span-12 md:col-span-7 text-editorial-muted font-light"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', lineHeight: '1.92' }}
            >
              {s.body}
            </div>
          </article>
        ))}
      </div>
    </div>
  </SectionPad>
);

const Regions = ({ copy, regions, localizedPath }) => (
  <SectionPad dark>
    <div className="max-w-6xl mx-auto">
      <div className="rv flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <div>
          <Eyebrow dark>{copy.regionsEyebrow}</Eyebrow>
          <h2
            className="text-editorial-bone font-light mt-7 max-w-2xl"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.2vw, 3rem)', lineHeight: '1.18' }}
          >
            {copy.regionsTitle}
          </h2>
        </div>
        <Link
          to={localizedPath('/regions')}
          className="text-editorial-bronze-lt hover:text-editorial-bone border-b border-editorial-bone/15 pb-0.5"
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.62rem', letterSpacing: '0.18em', textTransform: 'uppercase' }}
        >
          {copy.regionsCta}
        </Link>
      </div>
    </div>

    <div className="rv -mx-6 md:-mx-10 lg:-mx-16 xl:-mx-24 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
      <div className="flex gap-px px-6 md:px-10 lg:px-16 xl:px-24 pb-2" style={{ minWidth: 'max-content' }}>
        {regions.slice(0, 6).map((r) => {
          // Prefer the real region image from DB; fallback to placeholder map; final fallback hero.
          const rawImg = r.image || r.imageUrl || r.image_url;
          const dbImg = rawImg && !rawImg.startsWith('http') ? `${BACKEND_URL}${rawImg}` : rawImg;
          const img = dbImg || REGION_PLACEHOLDERS[r.name] || PLACEHOLDER.hero;
          return (
            <Link
              key={r.id || r.name}
              to={localizedPath('/regions')}
              className="region-card relative block flex-shrink-0 w-[300px] md:w-[360px] h-[440px] md:h-[500px] overflow-hidden group"
              data-testid={`region-${r.name}`}
            >
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                style={{ backgroundImage: `url(${img})` }}
              />
              <div
                className="absolute inset-0 transition-all duration-500 group-hover:brightness-90"
                style={{
                  background:
                    'linear-gradient(to top, rgba(18,16,12,0.85) 0%, rgba(18,16,12,0.08) 60%)',
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-7 md:p-9">
                <p
                  className="text-editorial-bronze-lt mb-3"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '0.6rem',
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                  }}
                >
                  {(r.character || 'Selected geography').slice(0, 40)}
                </p>
                <h3
                  className="text-editorial-bone font-light"
                  style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.85rem', lineHeight: '1.1' }}
                >
                  {r.name}
                </h3>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  </SectionPad>
);

const Opportunities = ({ copy, localizedPath }) => {
  const types = copy.opportunitiesTypes || {};
  return (
    <SectionPad>
      <div className="max-w-6xl mx-auto">
        <div className="rv mb-14">
          <Eyebrow>{copy.opportunitiesEyebrow}</Eyebrow>
          <h2
            className="text-editorial-charcoal font-light mt-7 max-w-2xl"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.2vw, 3rem)', lineHeight: '1.18' }}
          >
            {copy.opportunitiesTitle}
          </h2>
          <p
            className="text-editorial-muted mt-4 font-light max-w-xl"
            style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', lineHeight: '1.95' }}
          >
            {copy.opportunitiesSub}
          </p>
        </div>

        <div className="rv grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-px bg-editorial-sand-deep">
          {/* Large card — Residential acquisition */}
          <Link
            to={localizedPath('/contact')}
            className="relative h-[460px] overflow-hidden group bg-editorial-charcoal block"
            data-testid="mandate-residential"
          >
            <img
              src={PLACEHOLDER.opp1}
              alt={types.large?.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div
              className="absolute inset-0"
              style={{ background: 'linear-gradient(to top, rgba(18,16,12,0.92) 0%, rgba(18,16,12,0.32) 55%, transparent 100%)' }}
            />
            <div className="absolute bottom-0 left-0 right-0 p-9 md:p-11">
              <p
                className="text-editorial-bronze-lt mb-3"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.58rem', letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 500 }}
              >
                {types.large?.eyebrow}
              </p>
              <h3
                className="text-editorial-bone font-light mb-4"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', lineHeight: '1.12' }}
              >
                {types.large?.title}
              </h3>
              <p
                className="text-editorial-bone/65 font-light max-w-md mb-5"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.92rem', lineHeight: '1.85' }}
              >
                {types.large?.body}
              </p>
              <span
                className="inline-flex items-center text-editorial-bronze-lt group-hover:text-editorial-bone border-b border-editorial-bronze-lt/60 group-hover:border-editorial-bone pb-0.5 transition-colors"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.62rem', letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 500 }}
              >
                {copy.opportunitiesEnquiry} →
              </span>
            </div>
          </Link>

          {/* Two small cards stacked — Investment + Branded */}
          <div className="grid grid-rows-2 gap-px bg-editorial-sand-deep">
            {[
              { key: 'small1', img: PLACEHOLDER.opp2, data: types.small1 },
              { key: 'small2', img: PLACEHOLDER.opp3, data: types.small2 },
            ].map((o) => (
              <Link
                key={o.key}
                to={localizedPath('/contact')}
                className="relative h-[229px] overflow-hidden group bg-editorial-charcoal block"
                data-testid={`mandate-${o.key}`}
              >
                <img
                  src={o.img}
                  alt={o.data?.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(to top, rgba(18,16,12,0.92) 0%, rgba(18,16,12,0.32) 60%, transparent 100%)' }}
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-7">
                  <p
                    className="text-editorial-bronze-lt mb-2"
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.55rem', letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 500 }}
                  >
                    {o.data?.eyebrow}
                  </p>
                  <h3
                    className="text-editorial-bone font-light mb-1.5"
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem', lineHeight: '1.18' }}
                  >
                    {o.data?.title}
                  </h3>
                  <p
                    className="text-editorial-bone/60 font-light"
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', lineHeight: '1.55' }}
                  >
                    {o.data?.body}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </SectionPad>
  );
};

const Founders = ({ copy, image }) => (
  <SectionPad>
    <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
      <div className="rv lg:col-span-5">
        <div className="aspect-square overflow-hidden bg-editorial-sand">
          <img src={image} alt="Alex Kühne & Fernanda Stelzer" className="w-full h-full object-cover" style={{ filter: 'grayscale(0.08) contrast(1.02)' }} />
        </div>
      </div>
      <div className="lg:col-span-7">
        <div className="rv">
          <Eyebrow>{copy.foundersEyebrow}</Eyebrow>
        </div>
        <h2
          className="rv text-editorial-charcoal font-light mt-7 mb-9"
          style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.2vw, 3rem)', lineHeight: '1.18' }}
        >
          {copy.foundersTitle}
        </h2>
        <p
          className="rv text-editorial-muted font-light mb-10"
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', lineHeight: '1.95' }}
        >
          {copy.foundersBody}
        </p>
        <div className="rv grid grid-cols-1 sm:grid-cols-2 gap-6 border-t pt-8" style={{ borderColor: 'rgba(28,28,26,0.08)' }}>
          {[copy.foundersAlex, copy.foundersFer].map((p) => (
            <div key={p.name}>
              <h3 className="text-editorial-charcoal font-medium" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.35rem' }}>
                {p.name}
              </h3>
              <p className="text-editorial-muted mt-1 font-light" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', lineHeight: '1.88' }}>
                {p.role}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </SectionPad>
);

const Journal = ({ copy, posts, localizedPath }) => {
  if (!posts || posts.length === 0) return null;
  return (
    <SectionPad className="bg-editorial-sand/30">
      <div className="max-w-6xl mx-auto">
        <div className="rv flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <Eyebrow>{copy.journalEyebrow}</Eyebrow>
            <h2
              className="text-editorial-charcoal font-light mt-7 max-w-2xl"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 3.2vw, 3rem)', lineHeight: '1.18' }}
            >
              {copy.journalTitle}
            </h2>
          </div>
          <Link
            to={localizedPath('/journal')}
            className="text-editorial-charcoal hover:text-editorial-bronze border-b border-editorial-sand-deep pb-0.5"
            style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.62rem', letterSpacing: '0.18em', textTransform: 'uppercase' }}
          >
            {copy.journalCta}
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.slice(0, 3).map((p) => {
            const img = p.image && !p.image.startsWith('http') ? `${BACKEND_URL}${p.image}` : p.image || PLACEHOLDER.portugal;
            return (
              <Link key={p.id || p.slug} to={localizedPath(`/journal/${p.slug || p.id}`)} className="rv group block bg-editorial-mineral hover:bg-editorial-bone transition-colors">
                <img src={img} alt={p.title} className="w-full h-[230px] object-cover" />
                <div className="p-7">
                  {p.category && (
                    <p
                      className="text-editorial-bronze mb-3"
                      style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.58rem', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 500 }}
                    >
                      {p.category}
                    </p>
                  )}
                  <h3
                    className="text-editorial-charcoal mb-3"
                    style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.25rem', lineHeight: '1.35', fontWeight: 400 }}
                  >
                    {p.title}
                  </h3>
                  <span
                    className="text-editorial-charcoal border-b border-editorial-sand-deep pb-0.5"
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.58rem', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 500 }}
                  >
                    Read essay
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </SectionPad>
  );
};

const Closing = ({ copy, localizedPath, language }) => (
  <SectionPad dark className="!py-28 md:!py-40">
    <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center">
      <div className="rv">
        <h2
          className="text-editorial-bone font-light mb-7"
          style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2.2rem, 4vw, 3.6rem)', lineHeight: '1.08' }}
        >
          {copy.closingTitle}
        </h2>
        <p
          className="text-editorial-bone/42 font-light"
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', lineHeight: '1.95' }}
        >
          {copy.closingBody}
        </p>
      </div>
      <div className="rv flex flex-col gap-5 items-start">
        <a
          href={buildWhatsAppUrl('home', language)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-editorial-charcoal bg-editorial-sand hover:bg-editorial-bone transition-all duration-300 hover:-translate-y-0.5 py-4 px-10"
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.65rem', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 500, borderRadius: 0 }}
          data-testid="closing-cta-whatsapp"
        >
          {language === 'pt' ? 'Falar via WhatsApp' : 'Chat via WhatsApp'}
        </a>
        <Link
          to={localizedPath('/contact')}
          className="text-editorial-bone/60 hover:text-editorial-bone border-b border-editorial-bone/22 hover:border-editorial-bone pb-0.5 transition-colors"
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.65rem', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 500 }}
          data-testid="closing-cta"
        >
          {language === 'pt' ? 'Ou enviar pedido privado' : 'Or send a private enquiry'}
        </Link>
        <div className="w-full h-px bg-editorial-bone/7 my-2" />
        <div className="text-editorial-bone/3 space-y-2" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem', fontWeight: 300, lineHeight: '1.95' }}>
          <a href={`mailto:${copy.closingEmail}`} className="block text-editorial-bone/50 hover:text-editorial-bronze-lt border-b border-editorial-bone/13 pb-0.5">
            {copy.closingEmail}
          </a>
          <a href={`tel:${copy.closingPhone.replace(/\s/g, '')}`} className="block text-editorial-bone/50 hover:text-editorial-bronze-lt border-b border-editorial-bone/13 pb-0.5 mt-2">
            {copy.closingPhone}
          </a>
        </div>
        <p
          className="text-editorial-bone/18 mt-3"
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.7rem' }}
        >
          {copy.closingAmi}
        </p>
      </div>
    </div>
  </SectionPad>
);

// ─────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────

const EditorialHomePage = () => {
  const { language, localizedPath } = useLanguage();
  const copy = COPY[language] || COPY.pt;
  const [homeContent, setHomeContent] = useState(null);
  const [posts, setPosts] = useState([]);
  const [regions, setRegions] = useState([]);
  const ref = useRevealOnScroll([posts.length, regions.length, homeContent]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/home-content`).then((r) => setHomeContent(r.data)).catch(() => {});
    axios.get(`${BACKEND_URL}/api/blog`).then((r) => setPosts(r.data?.posts || [])).catch(() => setPosts([]));
    axios.get(`${BACKEND_URL}/api/regions`).then((r) => setRegions(r.data?.regions || r.data || [])).catch(() => setRegions([]));
  }, []);

  const heroImage = (homeContent?.hero?.image && !homeContent.hero.image.startsWith('http'))
    ? `${BACKEND_URL}${homeContent.hero.image}`
    : (homeContent?.hero?.image || PLACEHOLDER.hero);

  const foundersImage = PLACEHOLDER.founders;

  return (
    <>
      <SEO />
      <style>{`
        body { background: #f7f4ef; }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-track { background: #f7f4ef; }
        ::-webkit-scrollbar-thumb { background: #b8955a; }
        @keyframes heroZoom { from { transform: scale(1.07); } to { transform: scale(1.0); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: translateY(0); } }
        .rv { opacity: 0; transform: translateY(28px); transition: opacity 0.95s ease, transform 0.95s ease; }
        .rv.is-visible { opacity: 1; transform: translateY(0); }
        [data-testid="hero"] ~ * .rv { } /* no-op, placeholder */
      `}</style>
      <Header
        companyName={homeContent?.headerFooter?.companyName || 'Your Broker Portugal'}
        logo={homeContent?.headerFooter?.logo}
      />
      <main ref={ref}>
        <Hero image={heroImage} copy={copy} localizedPath={localizedPath} language={language} />
        <Manifesto copy={copy} />
        <Approach copy={copy} />
        <Regions copy={copy} regions={regions} localizedPath={localizedPath} />
        <Opportunities copy={copy} localizedPath={localizedPath} />
        <Founders copy={copy} image={foundersImage} />
        <Journal copy={copy} posts={posts} localizedPath={localizedPath} />
        <Closing copy={copy} localizedPath={localizedPath} language={language} />
      </main>
      <Footer
        data={{
          ...(homeContent?.headerFooter || {}),
          footerDescription:
            language === 'en'
              ? 'Private buying agent for international buyers in Portugal.'
              : 'Consultoria privada para compradores internacionais em Portugal.',
        }}
      />
    </>
  );
};

export default EditorialHomePage;
